import type {Dict} from "@plaudit/gutenberg-api-extensions";

export default abstract class QueryLoopFormFieldBase implements plaudit.queryLoop.QueryLoopFormField {
	private static readonly accordionOpenedClass = "plauditQueryLoopField__fieldArea--open";
	private static readonly fieldOpenedCache: Dict<boolean> = Object.create(null);
	
	public readonly name: string;
	private readonly detachActions: Array<() => void> = [];

	protected constructor(public readonly field: HTMLFieldSetElement, public readonly type: string) {
		this.name = field.getAttribute('data-name') ?? '';
		const form = field.form;
		if (form) {
			for (const element of field.querySelectorAll('.plauditQueryLoop__skipToResults')) {
				element.removeAttribute('hidden');
				element.addEventListener('click', e => {
					const targetElement = document.getElementById(`plaudit_query_results_${form.dataset['queryId']}`);
					if (targetElement) {
						e.preventDefault();
						targetElement.scrollIntoView();
						targetElement.focus();
					}
				});
			}

			field.addEventListener('click', e => {
				if (e.target instanceof Element) {
					if (e.target.classList.contains('plauditQueryLoopField__childrenToggleHook')) {
						const parent = e.target.closest('.plauditQueryLoopField--hasChildren');
						if (parent) {
							const expanded = parent.classList.toggle('plauditQueryLoopField--hasChildren-open');
							e.target.ariaExpanded = expanded ? 'true' : 'false';
							const ul = parent.querySelector(':scope > ul');
							if (ul) {
								ul.ariaHidden = expanded ? 'false' : 'true';
							}
							this.updateLabelForChildrenToggle(parent, expanded);
						}
					} else if (e.target.classList.contains('plauditQueryLoopField__resetButton')) {
						e.preventDefault();
						e.stopPropagation();
						form.dispatchEvent(new CustomEvent('reset', {bubbles: false, cancelable: true, detail: field.dataset['name']}));
						field.dispatchEvent(new Event('input', {bubbles: false}));
					}
				}
			});
		}

		field.addEventListener('input', () => {
			this.updateSelectionCounts();
			this.updateValueIsDefaultFlag();
		});
	}

	/**
	 * This *must* be called at the end of the constructor of implementing classes.
	 * @protected
	 */
	protected postConstruct() {
		this.updateSelectionCounts();
		if (this.isSingleValued()) {
			const params = new URLSearchParams(window.location.search);
			if (this.replaceMyPotentialPHPArrayParametersWithFirstValue(params)) {
				window.history.replaceState(window.history.state, '', `?${params}`);
			}
		}

		if (this.field.classList.contains("plauditQueryLoopField__fieldArea--collapsable")) {
			const resizeObserver = new ResizeObserver(entries => {
				for (const {target: element} of entries) {
					if (element instanceof HTMLElement && !element.classList.contains("fieldArea__content--transitioning") && element.style.maxHeight && element.style.maxHeight !== "0px") {
						if (QueryLoopFormFieldBase.elementPotentiallyInDialogIsVisible(element)) {
							element.style.maxHeight = ""; // We have to unset the maxHeight before recalculating the height, otherwise it can never increase
							element.style.maxHeight = `${element.getBoundingClientRect().height}px`; // We use element.getBoundingClientRect().height
						}
					}
				}
			});
			this.detachActions.push(() => resizeObserver.disconnect());

			const button = this.field.querySelector<HTMLButtonElement>(".fieldArea__heading__text button");
			if (button) {
				const initialState = QueryLoopFormFieldBase.fieldOpenedCache[this.field.id];
				if (initialState !== undefined) {
					this.field.classList.toggle(QueryLoopFormFieldBase.accordionOpenedClass, initialState);
				} else if (!this.field.classList.contains(QueryLoopFormFieldBase.accordionOpenedClass) && this.hasVisibleValue()) {
					this.field.classList.toggle(QueryLoopFormFieldBase.accordionOpenedClass, true);
				}
				this.applyCollapseState(button, this.field.classList.contains(QueryLoopFormFieldBase.accordionOpenedClass), false);

				button.addEventListener('click', () => {
					this.applyCollapseState(button, this.field.classList.toggle(QueryLoopFormFieldBase.accordionOpenedClass));
				});
			}

			for (const child of this.field.children) {
				if (child instanceof HTMLElement && child.classList.contains("fieldArea__content")) {
					resizeObserver.observe(child);

					child.addEventListener('transitionend', evt => {
						if (evt.propertyName === 'max-height' && evt.target === child) {
							if (child.style.maxHeight === "0px") {
								child.style.display = 'none';
							} else {
								child.classList.remove("fieldArea__content--transitioning");
							}
						}
					});
					break;
				}
			}
		}
	}

	/**
	 * This is used as a workaround for checkVisibility() only becoming official in 2024.
	 * The dialog-based check is likely to work but is imperfect, so we use checkVisibility() whenever we can.
	 */
	private static elementPotentiallyInDialogIsVisible(element: HTMLElement): boolean {
		if (typeof element.checkVisibility !== 'function') {
			const dialog = element.closest("dialog");
			return !dialog || dialog.open || dialog.closest(".dialog-mode-disabled") !== null;
		}
		return element.checkVisibility();
	}

	protected updateSelectionCounts() {
		const selectionCount = this.field.querySelectorAll('input.plauditQueryLoopField__input:checked').length;
		this.field.setAttribute('data-selection-count', selectionCount.toString());
		for (const ele of this.field.querySelectorAll('.fieldArea__selectionCount')) {
			ele.innerHTML = `(${selectionCount})`;
		}
	}

	protected applyCollapseState(button: HTMLButtonElement, open: boolean, markTransitioning = true) {
		button.setAttribute("aria-expanded", open.toString());

		const controlledElementId = button.getAttribute("aria-controls");
		if (!controlledElementId) {
			return;
		}
		const controlledElement = document.getElementById(controlledElementId);
		if (controlledElement) {
			if (markTransitioning) {
				controlledElement.classList.toggle("fieldArea__content--transitioning", true);
			}
			if (open) { // toggle returns true if we are opening the item
				controlledElement.style.display = '';
				controlledElement.style.maxHeight = `${controlledElement.scrollHeight}px`;
			} else {
				if (!markTransitioning) {
					controlledElement.style.display = 'none';
				}
				controlledElement.style.maxHeight = "0px";
			}
		}
		QueryLoopFormFieldBase.fieldOpenedCache[this.field.id] = open;
	}

	public abstract getValue(): string|string[];
	public abstract setValue(value: string|string[]): boolean;

	public remove(value: string): boolean {
		const current = this.getValue();
		if (typeof current === 'string') {
			const index = current.indexOf(value);
			return this.setValue(current.substring(0, index) + current.substring(index + value.length));
		} else {
			return this.setValue(current.filter(v => v !== value));
		}
	}

	public reset(): boolean {
		return this.setValue(this.getDefaultValue());
	}

	public hasVisibleValue(): boolean {
		return !!this.getValue()?.length;
	}

	public isTrackedInQuery(): boolean {
		return true;
	}

	public isAutoSubmitEnabled(): boolean {
		return this.field.getAttribute("data-auto-submit") !== 'off'
		&& this.field.closest(".plauditQueryLoopField__fieldArea")?.getAttribute("data-auto-submit") !== 'off';
	}

	public isValueEqual(value: string|string[]): boolean {
		const myValue = this.getValue();
		if (typeof value === 'string') {
			return value === myValue;
		}
		if (typeof myValue === 'string' || value.length !== myValue.length) {
			return false;
		}

		for (let i = 0; i < value.length; i++) {
			if (value[i] !== myValue[i]) {
				return false;
			}
		}
		return true;
	}

	public isSetToDefaultValue(): boolean {
		const value = this.getValue();
		const defaultValue = this.getDefaultValue();
		return value === defaultValue
			|| (Array.isArray(value) && Array.isArray(defaultValue) &&
				value.length === defaultValue.length && value.every(v => defaultValue.includes(v)));
	}

	public getLabel(): string {
		return this.field.querySelector('.fieldArea__heading')?.ariaLabel || this.name;
	}

	public abstract loadIntoQuery(params: URLSearchParams): boolean;
	public abstract loadFromQuery(params: URLSearchParams): boolean;

	protected loadIndividualValueIntoQuery(value: string, params: URLSearchParams) {
		if (value) {
			if (params.get(this.name) !== value) {
				params.set(this.name, value);
			}
			return true;
		} else {
			params.delete(this.name);
			return false;
		}
	}

	public abstract isSingleValued(): boolean;
	protected replaceMyPotentialPHPArrayParametersWithFirstValue(params: URLSearchParams) {
		const prefix = `${this.name}[`;
		let changed = false;
		let firstArrayValue: string|null = null;
		for (const [key, value] of params.entries()) {
			if (key.startsWith(prefix)) {
				if (firstArrayValue === null) {
					firstArrayValue = value;
				}
				changed = true;
				params.delete(key);
			}
		}
		if (firstArrayValue !== null) {
			params.set(this.name, firstArrayValue);
		}
		return changed;
	}

	public isInQuery(params = new URLSearchParams(window.location.search)) {
		if (params.has(this.name)) {
			return true;
		}
		if (this.isSingleValued()) {
			const prefix = `${this.name}[`;
			for (const key of params.keys()) {
				if (key.startsWith(prefix)) {
					return true;
				}
			}
		}
		return false;
	}

	public abstract getLabeledValues(selectedOnly?: boolean): plaudit.queryLoop.LabeledValue[];

	protected abstract getDefaultValue(): string|string[];

	protected updateValueIsDefaultFlag() {
		this.field.classList.toggle('valueIsDefault', this.isSetToDefaultValue());
	}

	protected findChildrenToggle(parent: Element): Element|undefined {
		// We use a breadth-first search instead of parent.querySelector() because we want the shallowest childrenToggle.
		const queue: Element[] = [parent];
		for (let node: Element|undefined; (node = queue.shift()) !== undefined;) {
			if (node.classList.contains("plauditQueryLoopField__childrenToggle")) {
				return node; // node will always be an HTMLElement
			} else {
				queue.push(...node.children);
			}
		}
		return undefined;
	}

	protected updateLabelForChildrenToggle(parent: Element, expanded: boolean) {
		const childrenToggle = this.findChildrenToggle(parent);
		if (childrenToggle) {
			childrenToggle.textContent = `${expanded ? "Hide" : "Show"} Children`;
		}
	}

	public isInDOM() {
		return document.body.contains(this.field);
	}
	public detach() {
		for (const detachAction of this.detachActions) {
			detachAction();
		}
	}
}
